"use client";

import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  format,
} from "date-fns";
import { ru } from "date-fns/locale";
import { Calendar as CalendarIcon, X } from "lucide-react";
import type { DateRange, SelectRangeEventHandler } from "react-day-picker";

import { Button } from "@/shared/components/ui/button";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { cn } from "@/shared/utils";
import { useEffect, useState } from "react";

interface DatePickerWithPresetsProps {
  dateFrom?: string;
  dateTo?: string;
  onChange?: (dateFrom: string, dateTo: string) => void;
}

export function DatePickerWithPresets({
  dateFrom,
  dateTo,
  onChange,
}: DatePickerWithPresetsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [date, setDate] = useState<DateRange | undefined>(() => {
    if (dateFrom) {
      return {
        from: new Date(dateFrom),
        to: dateTo ? new Date(dateTo) : undefined,
      };
    }
    return undefined;
  });
  const [month, setMonth] = useState<Date>(date?.from ?? new Date());

  useEffect(() => {
    if (!dateFrom && !dateTo) {
      setDate(undefined);
    }
  }, [dateFrom, dateTo]);
  console.log("month", month);

  // Утилита для вызова onChange
  const emitChange = (range: DateRange | undefined) => {
    if (!onChange) return;
    const from = range?.from ? format(range.from, "yyyy-MM-dd") : "";
    const to = range?.to ? format(range.to, "yyyy-MM-dd") : "";
    onChange(from, to);
  };

  // Обновить handleSelectDate — добавить emitChange при полном диапазоне:
  const handleSelectDate: SelectRangeEventHandler = (
    nextRange,
    selectedDay
  ) => {
    setDate((currentRange) => {
      if (
        (currentRange?.from && currentRange?.to) ||
        (currentRange?.from && selectedDay < currentRange.from)
      ) {
        emitChange({ from: selectedDay, to: undefined });
        return { from: selectedDay, to: undefined };
      }

      if (currentRange?.from && !currentRange?.to) {
        if (selectedDay.getTime() === currentRange.from.getTime()) {
          emitChange(undefined);
          return undefined;
        }
        const newRange = { from: currentRange.from, to: selectedDay };
        setTimeout(() => setIsOpen(false), 200);
        emitChange(newRange); // ← отправляем на бэк
        return newRange;
      }

      emitChange({ from: selectedDay, to: undefined });
      return { from: selectedDay, to: undefined };
    });
  };

  // Обновить handlePresetChange — добавить emitChange:
  const handlePresetChange = (value: string) => {
    const today = new Date();
    let newRange: DateRange;
    if (value === "today") newRange = { from: today, to: today };
    else if (value === "week")
      newRange = {
        from: startOfWeek(today, { weekStartsOn: 1 }),
        to: endOfWeek(today, { weekStartsOn: 1 }),
      };
    else newRange = { from: startOfMonth(today), to: endOfMonth(today) };

    setDate(newRange);
    setMonth(newRange.from as Date);
    emitChange(newRange); // ← отправляем на бэк
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {/* Делаем кнопку flex-контейнером с justify-between, чтобы разнести контент и крестик по краям */}
        <Button
          variant={"outline"}
          className={cn(
            "min-w-[260px] w-auto h-10 bg-white border-gray-200 hover:bg-gray-50 transition-colors flex items-center justify-between px-3 group",
            !date && "text-muted-foreground"
          )}
        >
          {/* Левая часть: Иконка календаря + Текст */}
          <div className="flex items-center gap-3 overflow-hidden">
            <CalendarIcon className="h-4 w-4 text-gray-500 shrink-0" />
            <span className="text-gray-900 text-sm font-medium whitespace-nowrap">
              {date?.from ? (
                date.to ? (
                  <>
                    {format(date.from, "dd.MM.yy", { locale: ru })} -{" "}
                    {format(date.to, "dd.MM.yy", { locale: ru })}
                  </>
                ) : (
                  format(date.from, "dd.MM.yyyy", { locale: ru })
                )
              ) : (
                "Даты"
              )}
            </span>
          </div>

          {/* Правая часть: Крестик сброса (отображается только если есть дата) */}
          {date && (
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDate(undefined);
                emitChange(undefined); // ← сбрасываем на бэке
              }}
              className="p-1 -mr-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex items-center justify-center shrink-0 focus:outline-none"
            >
              <X className="h-4 w-4" />
            </div>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="flex w-auto flex-col p-3 rounded-xl border border-gray-200 shadow-lg bg-white"
        align="start"
      >
        <div className="mb-2">
          <Select onValueChange={handlePresetChange}>
            <SelectTrigger className="w-full border-none shadow-none focus:ring-0 text-base text-gray-600 hover:text-gray-900 px-2 h-9">
              <SelectValue placeholder="Быстрый выбор" />
            </SelectTrigger>
            <SelectContent
              position="popper"
              className="rounded-xl shadow-md border-gray-100"
            >
              <SelectItem value="today" className="cursor-pointer rounded-lg">
                Сегодня
              </SelectItem>
              <SelectItem value="week" className="cursor-pointer rounded-lg">
                Эта неделя
              </SelectItem>
              <SelectItem value="month" className="cursor-pointer rounded-lg">
                Этот месяц
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="h-[1px] bg-gray-100 w-full mb-2" />

        <div>
          <Calendar
            initialFocus
            mode="range"
            month={month} // ← управляемый
            onMonthChange={setMonth}
            selected={date}
            onSelect={handleSelectDate}
            numberOfMonths={1}
            locale={ru}
            className="p-0"
            components={{
              Chevron: (props) => {
                if (props.orientation === "left") {
                  return (
                    <span className="text-gray-500 text-[10px] leading-none select-none">
                      {"<"}
                    </span>
                  );
                }
                return (
                  <span className="text-gray-500 text-[10px] leading-none select-none">
                    {">"}
                  </span>
                );
              },
            }}
            classNames={{
              // Обертка календаря
              months:
                "relative flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 mt-2",
              month: "space-y-4 w-full",

              // ЗАГОЛОВОК МЕСЯЦА (Сделали flex-between чтобы раскидать заголовок и кнопки по одной линии)
              month_caption: "flex justify-center relative items-center h-8",
              caption_label: "text-sm font-semibold",

              // КНОПКИ НАВИГАЦИИ (Расставляем строго слева и справа от заголовка месяца)
              nav: "absolute inset-x-0 top-0 flex items-center justify-between z-50",
              nav_button_previous:
                "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 flex items-center justify-center rounded-md hover:bg-gray-100 border border-gray-200 pointer-events-auto",
              nav_button_next:
                "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 flex items-center justify-center rounded-md hover:bg-gray-100 border border-gray-200 pointer-events-auto",

              month_grid: "w-full border-collapse space-y-1 mx-auto",
              weekdays: "flex",
              weekday:
                "text-muted-foreground w-9 font-medium text-[0.85rem] lowercase mb-1 text-center",
              week: "flex w-full mt-2",

              // ЯЧЕЙКА И ДИАПАЗОН (светло-синий фон)
              day: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-full [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-blue-50 first:[&:has([aria-selected])]:rounded-l-full last:[&:has([aria-selected])]:rounded-r-full focus-within:relative focus-within:z-20",

              // КНОПКА ДНЯ (Убрали hover-эффекты для выбранных дней внутри диапазона)
              day_button:
                "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center aria-selected:hover:bg-transparent",

              // НАЧАЛО И КОНЕЦ (Темно-синий фон. Важно: !bg-blue-600 перебивает любые ховеры)
              range_start:
                "day-range-start aria-selected:!bg-blue-600 aria-selected:!text-white hover:!bg-blue-600",
              range_end:
                "day-range-end aria-selected:!bg-blue-600 aria-selected:!text-white hover:!bg-blue-600",

              // ДНИ МЕЖДУ НАЧАЛОМ И КОНЦОМ (Без фона, только синий текст)
              range_middle:
                "aria-selected:!bg-transparent aria-selected:!text-blue-600 hover:!bg-transparent hover:!text-blue-600",

              // ВЫБРАННЫЙ ДЕНЬ (до создания диапазона)
              selected:
                "bg-blue-600 text-white hover:!bg-blue-600 hover:!text-white focus:bg-blue-600",

              today: "bg-gray-100 text-gray-900",
              outside:
                "day-outside text-gray-400 opacity-50 aria-selected:bg-blue-50/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
